import React from 'react';
import {
  MDDAP_CHECK_POINTDOCUMENT_TYPES,
  checkPointDocumentsMap
} from '../../constants/mddap-checkpoint-document-types';
import { REAPPRAISAL_RATIONALE_REVIEW_DISPLAY_NAMES } from '../../../reappraisalRationaleReview/constants/reappraisalRationaleReviewConstants';
import { PAGE_VIEW_MODES } from '../../../../constants/page-view-modes';
import ErrorMessage from '@shared/components/ErrorMessage';
import _ from 'lodash';
import StyledTable from '@shared/components/StyledTable';
import cx from 'classnames';

const CHECKPOINT_FILES_TABLE_ROW_METADATA = [
  {
    documentType: MDDAP_CHECK_POINTDOCUMENT_TYPES.CheckPointReport,
    category: checkPointDocumentsMap.get(MDDAP_CHECK_POINTDOCUMENT_TYPES.CheckPointReport).Name,
    isRequiredDocumentType: true
  },
  {
    documentType: MDDAP_CHECK_POINTDOCUMENT_TYPES.ReappraisalRationale,
    category: checkPointDocumentsMap.get(MDDAP_CHECK_POINTDOCUMENT_TYPES.ReappraisalRationale).Name,
    isRequiredDocumentType: true
  },
  {
    documentType: MDDAP_CHECK_POINTDOCUMENT_TYPES.OtherFirst,
    category: checkPointDocumentsMap.get(MDDAP_CHECK_POINTDOCUMENT_TYPES.OtherFirst).Name,
    isRequiredDocumentType: false
  },
  {
    documentType: MDDAP_CHECK_POINTDOCUMENT_TYPES.OtherSecond,
    category: checkPointDocumentsMap.get(MDDAP_CHECK_POINTDOCUMENT_TYPES.OtherSecond).Name,
    isRequiredDocumentType: false
  },
  {
    documentType: MDDAP_CHECK_POINTDOCUMENT_TYPES.OtherThird,
    category: checkPointDocumentsMap.get(MDDAP_CHECK_POINTDOCUMENT_TYPES.OtherThird).Name,
    isRequiredDocumentType: false
  }
];

/**
 * @typedef {object} CheckpointFilesTableRow
 * @property {number} documentType
 * @property {string} category
 * @property {string} key
 * @property {boolean} hideActions
 * @property {string} [fileName]
 * @property {number} [id]
 */

/**
 * @typedef {object} CheckpointFilesTableProps
 * @property {string} viewMode
 * @property {(event: React.ChangeEvent<HTMLInputElement>, record: CheckpointFilesTableRow) => void} [handleFileInputClicked]
 * @property {(event: React.MouseEvent, record: CheckpointFilesTableRow) => void | Promise<void>} handleFileDownload
 * @property {(event: React.MouseEvent, record: CheckpointFilesTableRow) => void} [handleFileRemove]
 * @property {Array<*>} checkPointDocuments
 * @property {string} [fileErrorMessage]
 * @property {boolean} [canUploadRequiredFiles]
 * @property {Array<*>} [requiredTypes]
 * @property {boolean} [loading]
 * @property {Array<Object>} [documentValidationErrors]
 */

/**
 * @param {CheckpointFilesTableProps} props
 */
const CheckpointFilesTable = props => {
  const {
    viewMode,
    handleFileInputClicked,
    handleFileDownload,
    handleFileRemove,
    checkPointDocuments,
    fileErrorMessage,
    documentValidationErrors = [],
    canUploadRequiredFiles = true,
    requiredTypes = [],
    loading = false
  } = props;

  const isReappraisalRationaleUploaded = checkPointDocuments.some(
    x => x.documentType === MDDAP_CHECK_POINTDOCUMENT_TYPES.ReappraisalRationale
  );

  const columns = [
    {
      title: 'Category',
      dataIndex: 'category'
    },
    {
      title: 'Status',
      width: '20%',
      dataIndex: 'reviewStatus',
      render: reviewStatus => REAPPRAISAL_RATIONALE_REVIEW_DISPLAY_NAMES.get(reviewStatus),
      hidden: !(viewMode === PAGE_VIEW_MODES.VIEW && isReappraisalRationaleUploaded)
    },
    {
      title: 'File Name',
      dataIndex: 'fileName',
      width: '40%',
      render: (fileName, record) => {
        return !fileName ? (
          <div className={cx({ error: hasError(record) })}>-</div>
        ) : (
          <a onClick={async e => await handleFileDownload(e, record)}>{fileName}</a>
        );
      }
    },
    {
      title: 'Action',
      width: '20%',
      render: (_, record) => {
        if (record.hideActions) {
          return null;
        }

        return !record.fileName ? (
          <>
            <a onClick={e => handleUploadClick(e, record)}>Upload</a>
            <input
              type='file'
              accept={checkPointDocumentsMap
                .get(record.documentType)
                .AllowedTypes.map(x => '.' + x.name)
                .join(', ')}
              id={`file-upload-${record.key}`}
              data-testid={`file-upload-${record.key}`}
              className='input-file-upload'
              onClick={e => {
                e.currentTarget.value = '';
              }}
              onChange={e => {
                handleFileInputClicked(e, record);
              }}
            />
          </>
        ) : (
          <a data-testid={`remove-file-${record.key}`} onClick={e => handleFileRemove(e, record)}>
            Remove
          </a>
        );
      },
      hidden: viewMode === PAGE_VIEW_MODES.VIEW
    }
  ];

  const tableRowsMetadata = CHECKPOINT_FILES_TABLE_ROW_METADATA.map(
    ({ documentType, category, isRequiredDocumentType }) => ({
      documentType,
      category,
      isReadOnlyDocument: isRequiredDocumentType && !canUploadRequiredFiles
    })
  );

  const hasError = row => {
    return documentValidationErrors.some(x => x.documentType == row.documentType);
  };

  const rows = getCheckpointFilesTableRows({
    checkPointDocuments,
    tableRowsMetadata,
    viewMode,
    requiredTypes
  });

  const visibleColumns = columns.filter(item => !item.hidden);

  const handleUploadClick = (e, record) => {
    document.getElementById(`file-upload-${record.key}`).click();
    e.preventDefault();
  };

  return (
    <div className='check-point-files-table' data-testid='checkpoint-files-table'>
      <p className='bold' data-testid='uploaded-files-count'>
        Check Point Files
        {viewMode === PAGE_VIEW_MODES.ADD || viewMode === PAGE_VIEW_MODES.EDIT
          ? `: ${checkPointDocuments.length} of ${rows.length}`
          : ''}
      </p>
      {fileErrorMessage && (
        <ErrorMessage
          dataTestId='file-error-message'
          className='error-message'
          message={fileErrorMessage}
        />
      )}
      <StyledTable
        columns={visibleColumns}
        dataSource={rows}
        pagination={false}
        showSorterTooltip={false}
        loading={loading}
        onRow={(_, documentType) => ({
          'data-testid': `row-${documentType}`
        })}
      />
    </div>
  );
};

const getCheckpointFilesTableRows = ({
  checkPointDocuments,
  tableRowsMetadata,
  viewMode,
  requiredTypes
}) => {
  const rowsForExistingFiles = buildExistingFileRows(
    checkPointDocuments,
    tableRowsMetadata,
    requiredTypes
  );

  const emptyRows =
    viewMode === PAGE_VIEW_MODES.VIEW
      ? []
      : buildEmptyRows(
          tableRowsMetadata,
          rowsForExistingFiles.map(r => r.documentType),
          requiredTypes
        );

  return _.sortBy([...rowsForExistingFiles, ...emptyRows], r => r.documentType);
};

const buildExistingFileRows = (checkPointDocuments, tableRowsMetadata, requiredTypes) => {
  return checkPointDocuments.map((cpd, index) =>
    buildRow(cpd, tableRowsMetadata, requiredTypes, `row-${index}`)
  );
};

const buildEmptyRows = (tableRowsMetadata, existingDocumentTypes, requiredTypes) => {
  const emptyMetadata = tableRowsMetadata.filter(
    mt => !mt.isReadOnlyDocument && !existingDocumentTypes.includes(mt.documentType)
  );

  return emptyMetadata.map((metadata, index) =>
    buildRow(
      { documentType: metadata.documentType, fileName: '' },
      tableRowsMetadata,
      requiredTypes,
      `empty-row-${index}`
    )
  );
};

const buildRow = (document, tableRowsMetadata, requiredTypes, key) => {
  const metadata = tableRowsMetadata.find(mt => mt.documentType === document.documentType);

  return {
    ...document,
    category: metadata.category + getAsteriskIfFieldRequired(document.documentType, requiredTypes),
    key,
    hideActions: metadata.isReadOnlyDocument
  };
};

const getAsteriskIfFieldRequired = (documentType, requiredTypes) =>
  requiredTypes.includes(documentType) ? '*' : '';

export default CheckpointFilesTable;
